import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ChannelPost from '../components/ChannelPost';
import QuickConnectModal from '../components/QuickConnectModal';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';
import './ChannelFeed.css';

const CATEGORIES = ['all', 'general', 'news', 'story', 'announcement', 'discussion'];

function ChannelFeed() {
  const { channelId, slug: urlSlug } = useParams();
  const { isOrgUser, orgSlug } = useOrg();
  const { user } = useAuth();
  const slug = urlSlug || orgSlug;
  const p = (path) => (isOrgUser && slug) ? `/org/${slug}${path}` : path;
  const navigate = useNavigate();
  const [channel, setChannel] = useState(null);
  const [posts, setPosts] = useState([]);
  const [coldStart, setColdStart] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostCategory, setNewPostCategory] = useState('general');
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState('');
  const [loading, setLoading] = useState(true);
  const [connectModalPost, setConnectModalPost] = useState(null);

  const token = localStorage.getItem('accessToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchChannel();
    fetchPosts(1, 'all');
  }, [channelId]);

  const fetchChannel = async () => {
    try {
      const res = await axios.get(`/api/channels/${channelId}`, { headers });
      setChannel(res.data.channel || res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        // User doesn't have this identity — redirect to public read-only view
        const slug = err.response?.data?.slug;
        if (slug) {
          navigate(`/b/${slug}?readonly=1`, { replace: true });
        } else {
          // Fallback: try to get slug from a public endpoint
          try {
            const slugRes = await axios.get(`/api/channels/${channelId}/slug`);
            if (slugRes.data?.slug) {
              navigate(`/b/${slugRes.data.slug}?readonly=1`, { replace: true });
            } else {
              navigate('/dashboard', { replace: true });
            }
          } catch {
            navigate('/dashboard', { replace: true });
          }
        }
        return;
      }
      console.error('Error fetching channel:', err);
    }
  };

  const fetchPosts = async (pageNum, category) => {
    try {
      setLoading(true);
      const params = { page: pageNum };
      if (category && category !== 'all') params.category = category;
      const res = await axios.get(`/api/channels/${channelId}/posts`, { headers, params });
      const data = res.data;
      const newPosts = data.posts || [];
      if (pageNum === 1) {
        setPosts(newPosts);
      } else {
        setPosts(prev => [...prev, ...newPosts]);
      }
      setHasMore(newPosts.length === 20);
      if (data.coldStart) setColdStart(data.coldStart);
      else if (data.cold_start) setColdStart(data.cold_start);
      else setColdStart(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (category) => {
    setActiveFilter(category);
    setPage(1);
    fetchPosts(1, category);
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage, activeFilter);
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;
    setPosting(true);
    setPostError('');
    try {
      await axios.post(`/api/channels/${channelId}/posts`, {
        content: newPostContent.trim(),
        category: newPostCategory
      }, { headers });
      setNewPostContent('');
      setNewPostCategory('general');
      fetchPosts(1, activeFilter);
    } catch (err) {
      setPostError(err.response?.data?.error || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  const handleColdStartPrompt = (promptText) => {
    setNewPostContent(promptText);
    const composer = document.querySelector('.channel-feed-composer textarea');
    if (composer) composer.focus();
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`/api/channels/${channelId}/posts/${postId}/react`, {}, { headers });
      setPosts(prev => prev.map(p =>
        p.post_id === postId
          ? { ...p, user_liked: res.data.liked, like_count: res.data.like_count }
          : p
      ));
    } catch (err) {
      console.error('Error toggling like:', err);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`/api/channels/${channelId}/posts/${postId}`, { headers });
      setPosts(prev => prev.filter(p => p.post_id !== postId));
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleReport = async (postId) => {
    const reason = window.prompt('Why are you reporting this post?');
    if (!reason) return;
    try {
      await axios.post(`/api/channels/${channelId}/posts/${postId}/report`, { reason }, { headers });
      alert('Post reported. Thank you.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to report post');
    }
  };

  const handleConnect = (post) => {
    setConnectModalPost(post);
  };

  const handleConnectSend = async (message) => {
    if (!connectModalPost) return;
    try {
      await axios.post(
        `/api/channels/${channelId}/posts/${connectModalPost.post_id}/connect`,
        { message },
        { headers }
      );
      alert('Connection request sent! 🎉');
      setConnectModalPost(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send connection request');
      throw err; // Let modal know it failed
    }
  };

  const handleEdit = async (post) => {
    const newContent = window.prompt('Edit your post:', post.content);
    if (!newContent || newContent.trim() === post.content) return;
    try {
      await axios.put(`/api/channels/${channelId}/posts/${post.post_id}`, {
        content: newContent.trim()
      }, { headers });
      fetchPosts(1, activeFilter);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to edit post');
    }
  };

  const handleCommentAdded = (postId) => {
    setPosts(prev => prev.map(p =>
      p.post_id === postId ? { ...p, comment_count: (p.comment_count || 0) + 1 } : p
    ));
  };

  const memberDisplay = channel
    ? (typeof channel.member_count === 'string' ? channel.member_count : `${channel.member_count} members`)
    : '';

  return (
    <div>
      <Navbar />
      <div className="container">
        <button className="channel-feed-back-btn" onClick={() => navigate(p('/dashboard'))}>
          ← Back to Dashboard
        </button>

        {channel && (
          <div className="channel-feed-header">
            <h2>{channel.category_icon || '💬'} {channel.name}</h2>
            <div className="channel-feed-header-meta">{memberDisplay}</div>
          </div>
        )}

        {isOrgUser && slug && (
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '1px solid #bbf7d0',
            borderRadius: '8px',
            padding: '10px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: '#166534',
          }}>
            <span>🔒</span>
            <span>Private <strong>{slug}</strong> space — only visible to your organization's employees. Your posts are anonymous.</span>
          </div>
        )}

        {channel && channel.small_channel_notice && (
          <div className="channel-feed-small-notice">
            ⚠️ This is a small community. Your posts are anonymous, but others may be able to infer who is posting.
          </div>
        )}

        {coldStart && coldStart.showColdStart && (
          <div className="channel-feed-cold-start">
            <h3>👋 {coldStart.welcomeMessage}</h3>
            <p>Get the conversation started! Here are some ideas:</p>
            <div className="channel-feed-prompts">
              {(coldStart.prompts || []).map((prompt, i) => (
                <div
                  key={i}
                  className="channel-feed-prompt-card"
                  onClick={() => handleColdStartPrompt(prompt)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleColdStartPrompt(prompt)}
                >
                  💡 {prompt}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="channel-feed-filters">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`channel-feed-filter-btn ${activeFilter === cat ? 'active' : ''}`}
              onClick={() => handleFilterChange(cat)}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        <div className="channel-feed-composer">
          <form onSubmit={handleSubmitPost}>
            <textarea
              placeholder="Share something with your community..."
              value={newPostContent}
              onChange={(e) => setNewPostContent(e.target.value)}
              maxLength={2000}
            />
            <div className="channel-feed-composer-actions">
              <select
                value={newPostCategory}
                onChange={(e) => setNewPostCategory(e.target.value)}
                aria-label="Post category"
              >
                <option value="general">General</option>
                <option value="news">News</option>
                <option value="story">Story</option>
                <option value="announcement">Announcement</option>
                <option value="discussion">Discussion</option>
              </select>
              <button type="submit" className="btn btn-primary" disabled={posting || !newPostContent.trim()}>
                {posting ? 'Posting...' : 'Post'}
              </button>
            </div>
            {postError && <div className="error" style={{ marginTop: '8px' }}>{postError}</div>}
          </form>
        </div>

        {loading && posts.length === 0 ? (
          <div className="channel-feed-empty">
            <div className="channel-feed-empty-icon">⏳</div>
            <p>Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="channel-feed-empty">
            <div className="channel-feed-empty-icon">📝</div>
            <p>No posts yet. Be the first to share!</p>
          </div>
        ) : (
          <>
            {posts.map(post => (
              <ChannelPost
                key={post.post_id}
                post={post}
                channelId={channelId}
                onLike={handleLike}
                onConnect={handleConnect}
                onReport={handleReport}
                onDelete={handleDelete}
                onEdit={handleEdit}
                onCommentAdded={handleCommentAdded}
                isAdmin={user?.is_admin}
              />
            ))}
            {hasMore && (
              <button className="channel-feed-load-more" onClick={handleLoadMore}>
                Load more posts
              </button>
            )}
          </>
        )}
      </div>

      {/* Quick Connect Modal */}
      <QuickConnectModal
        isOpen={!!connectModalPost}
        onClose={() => setConnectModalPost(null)}
        onConnect={handleConnectSend}
        postPreview={connectModalPost?.content}
        channelName={channel?.name}
      />
    </div>
  );
}

export default ChannelFeed;
